import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../App";
import {
  FileText,
  Clock,
  AlertTriangle,
  Users,
  GitBranch,
  ChevronRight,
  Calendar,
  CheckCircle2,
  AlertCircle,
  MapPin,
  Home
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { format, parseISO, isAfter, isBefore } from "date-fns";

// Parse date string safely to avoid timezone drift
const formatDateSafe = (dateStr, fmt = 'MMM d, yyyy') => {
  if (!dateStr) return '';
  const datePart = dateStr.split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  return format(new Date(y, m - 1, d), fmt);
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)] tracking-tight">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-[var(--t-text-muted)] mt-1">Here's what's happening with your transactions today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8" data-testid="stats-grid">
        <Card className="card-surface cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-active-listings" onClick={() => navigate("/transactions?status=active_listing")}>
          <CardContent className="pt-6 pb-2">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[var(--t-text-muted)] font-medium">Active Listings</p>
                <p className="stat-value mt-2">{stats?.transactions?.active_listing || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-md">
                <Home className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            {stats?.transactions?.active_listing_properties?.length > 0 && (
              <div className="mt-3 pt-2 border-t border-[var(--t-border-light)] space-y-1">
                {stats.transactions.active_listing_properties.slice(0, 5).map((p) => (
                  <Link
                    key={p.transaction_id}
                    to={`/transactions/${p.transaction_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 py-1 px-1 -mx-1 rounded hover:bg-blue-50 transition-colors group"
                    data-testid={`listing-prop-${p.transaction_id}`}
                  >
                    <Home className="w-3 h-3 text-blue-400 flex-shrink-0" />
                    <p className="text-xs text-[var(--t-text)] truncate group-hover:text-blue-700">{p.property_address}</p>
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-[var(--t-border-light)] text-center">
              <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">View All Active Listings</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-surface cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-active-transactions" onClick={() => navigate("/transactions?status=active")}>
          <CardContent className="pt-6 pb-2">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[var(--t-text-muted)] font-medium">Properties Before Inspections</p>
                <p className="stat-value mt-2">{stats?.transactions?.active || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-md">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
            </div>
            {stats?.transactions?.active_properties?.length > 0 && (
              <div className="mt-3 pt-2 border-t border-[var(--t-border-light)] space-y-1">
                {stats.transactions.active_properties.slice(0, 5).map((p) => (
                  <Link
                    key={p.transaction_id}
                    to={`/transactions/${p.transaction_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 py-1 px-1 -mx-1 rounded hover:bg-green-50 transition-colors group"
                    data-testid={`active-prop-${p.transaction_id}`}
                  >
                    <FileText className="w-3 h-3 text-green-400 flex-shrink-0" />
                    <p className="text-xs text-[var(--t-text)] truncate group-hover:text-green-700">{p.property_address}</p>
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-[var(--t-border-light)] text-center">
              <span className="text-[10px] font-semibold text-green-600 uppercase tracking-wider">View All Active</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-surface cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-pending-transactions" onClick={() => navigate("/transactions?status=through_inspections")}>
          <CardContent className="pt-6 pb-2">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[var(--t-text-muted)] font-medium">Properties Through Inspections</p>
                <p className="stat-value mt-2">{stats?.transactions?.through_inspections || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-md">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            {stats?.transactions?.through_properties?.length > 0 && (
              <div className="mt-3 pt-2 border-t border-[var(--t-border-light)] space-y-1">
                {stats.transactions.through_properties.slice(0, 5).map((p) => (
                  <Link
                    key={p.transaction_id}
                    to={`/transactions/${p.transaction_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 py-1 px-1 -mx-1 rounded hover:bg-yellow-50 transition-colors group"
                    data-testid={`through-prop-${p.transaction_id}`}
                  >
                    <Clock className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                    <p className="text-xs text-[var(--t-text)] truncate group-hover:text-yellow-700">{p.property_address}</p>
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-[var(--t-border-light)] text-center">
              <span className="text-[10px] font-semibold text-yellow-600 uppercase tracking-wider">View All Properties Through Inspections</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-surface cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-overdue-tasks" onClick={() => navigate("/tasks?filter=overdue")}>
          <CardContent className="pt-6 pb-2">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[var(--t-text-muted)] font-medium">Overdue Tasks</p>
                <p className="stat-value mt-2 text-red-600">{stats?.tasks?.overdue_count || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-md">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            {stats?.tasks?.overdue?.length > 0 && (
              <div className="mt-3 border-t border-[var(--t-border-light)] pt-2 space-y-1">
                {stats.tasks.overdue.slice(0, 5).map((task) => (
                  <Link
                    key={task.task_id}
                    to={`/transactions/${task.transaction_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 py-1.5 px-1 -mx-1 rounded hover:bg-red-50 transition-colors group"
                    data-testid={`overdue-stat-task-${task.task_id}`}
                  >
                    <AlertCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[var(--t-text)] truncate group-hover:text-red-700">{task.title}</p>
                      <p className="text-[10px] text-[var(--t-text-subtle)] truncate">{task.property_address}</p>
                    </div>
                    <span className="text-[10px] text-red-500 flex-shrink-0">{formatDateSafe(task.due_date, 'M/d')}</span>
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-[var(--t-border-light)] text-center">
              <span className="text-[10px] font-semibold text-red-600 uppercase tracking-wider">View All Overdue</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-surface cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-upcoming-tasks" onClick={() => navigate("/tasks?filter=upcoming")}>
          <CardContent className="pt-6 pb-2">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[var(--t-text-muted)] font-medium">Upcoming Tasks</p>
                <p className="stat-value mt-2">{stats?.tasks?.upcoming_count || 0}</p>
              </div>
              <div className="p-3 bg-[var(--t-text)]/10 rounded-md">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            {stats?.tasks?.upcoming?.length > 0 && (
              <div className="mt-3 border-t border-[var(--t-border-light)] pt-2 space-y-1">
                {stats.tasks.upcoming.slice(0, 5).map((task) => (
                  <Link
                    key={task.task_id}
                    to={`/transactions/${task.transaction_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 py-1.5 px-1 -mx-1 rounded hover:bg-blue-50 transition-colors group"
                    data-testid={`upcoming-stat-task-${task.task_id}`}
                  >
                    <Calendar className="w-3 h-3 text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[var(--t-text)] truncate group-hover:text-blue-700">{task.title}</p>
                      <p className="text-[10px] text-[var(--t-text-subtle)] truncate">{task.property_address}</p>
                    </div>
                    <span className="text-[10px] text-blue-500 flex-shrink-0">{formatDateSafe(task.due_date, 'M/d')}</span>
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-[var(--t-border-light)] text-center">
              <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">View All Upcoming</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks Overview */}
        <Card className="card-surface lg:col-span-2" data-testid="upcoming-tasks-card">
          <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">
                Tasks Due Today
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {stats?.tasks?.today_count || 0} tasks
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {stats?.tasks?.today?.length > 0 ? (
              <div>
                <div className="divide-y divide-slate-100">
                  {stats.tasks.today.map((task, index) => (
                    <Link
                      key={task.task_id}
                      to={`/transactions/${task.transaction_id}`}
                      className="flex items-center gap-4 px-5 py-3 hover:bg-blue-50/50 transition-colors"
                      data-testid={`today-task-${index}`}
                    >
                      <div className="p-2 rounded-md bg-[var(--t-text)]/10">
                        <Calendar className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--t-text)] truncate">{task.title}</p>
                        <p className="text-xs text-[var(--t-text-muted)] truncate">
                          <MapPin className="w-3 h-3 inline mr-1" />{task.property_address}
                          {task.assigned_coordinator_name && (
                            <span className="ml-2 text-[var(--t-text-subtle)]">· {task.assigned_coordinator_name}</span>
                          )}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4" data-testid="all-caught-up">
                <CheckCircle2 className="w-10 h-10 mb-3" style={{ color: "var(--t-primary)", opacity: 0.6 }} />
                <p className="text-lg font-semibold" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }}>All caught up!</p>
                <p className="text-sm mt-1" style={{ color: "var(--t-text-muted)" }}>No tasks due today.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="card-surface" data-testid="quick-actions-card">
          <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
            <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-3">
            <Link to="/transactions?action=new" className="block">
              <Button 
                className="w-full justify-start btn-primary"
                data-testid="quick-new-transaction"
              >
                <FileText className="w-4 h-4 mr-2" />
                New Transaction
              </Button>
            </Link>
            <Link to="/workflows?action=new" className="block">
              <Button 
                variant="outline" 
                className="w-full justify-start btn-secondary"
                data-testid="quick-new-workflow"
              >
                <GitBranch className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            </Link>
            <Link to="/contacts?action=new" className="block">
              <Button 
                variant="outline" 
                className="w-full justify-start btn-secondary"
                data-testid="quick-new-contact"
              >
                <Users className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </Link>
          </CardContent>

          {/* Transaction Summary */}
          <div className="px-5 pb-5">
            <div className="p-4 bg-[var(--t-bg)] rounded-md border border-[var(--t-border)]">
              <p className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] font-semibold mb-3">
                Transaction Summary
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--t-text-muted)]">Active Listings</span>
                  <span className="text-sm font-semibold text-[var(--t-text)]">{stats?.transactions?.active_listing || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--t-text-muted)]">Properties Before Inspections</span>
                  <span className="text-sm font-semibold text-[var(--t-text)]">{stats?.transactions?.active || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--t-text-muted)]">Properties Through Inspections</span>
                  <span className="text-sm font-semibold text-[var(--t-text)]">{stats?.transactions?.pending || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--t-text-muted)]">Closed</span>
                  <span className="text-sm font-semibold text-[var(--t-text)]">{stats?.transactions?.closed || 0}</span>
                </div>
                <div className="border-t border-[var(--t-border)] pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[var(--t-text)]">Total</span>
                    <span className="text-sm font-bold text-[var(--t-primary)]">{stats?.transactions?.total || 0}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-[var(--t-text-subtle)]">Cancelled</span>
                  <span className="text-sm font-semibold text-[var(--t-text-subtle)]">{stats?.transactions?.cancelled || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
